import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { canUserReview, submitBrokerReview, REVIEW_TEST_MODE } from '../lib/brokerReviews';
import StarRating from './StarRating';
import './LeaveReviewForm.css';

const IcCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IcWarn = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IcLogin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const MAX_CHARS = 1000;
const MIN_CHARS = 20;

export default function LeaveReviewForm({ brokerId, userId, onReviewSubmitted }) {
  const [eligibility, setEligibility] = useState(null);
  const [rating, setRating]       = useState(0);
  const [text, setText]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [error, setError]         = useState('');

  const checkEligibility = useCallback(() => {
    canUserReview(brokerId, userId).then(setEligibility);
  }, [brokerId, userId]);

  useEffect(() => { checkEligibility(); }, [checkEligibility]);

  const handleSubmit = async () => {
    if (rating === 0) { setError('Please select a star rating.'); return; }
    if (text.trim().length < MIN_CHARS) { setError(`Review must be at least ${MIN_CHARS} characters.`); return; }
    setError('');
    setSubmitting(true);

    const result = await submitBrokerReview({
      brokerId,
      reviewerId: userId,
      rating,
      reviewText: text.trim(),
    });

    setSubmitting(false);

    if (!result.success) {
      setError(result.error || 'Something went wrong. Please try again.');
      return;
    }

    setSuccess(true);
    if (onReviewSubmitted) onReviewSubmitted(result.review);
  };

  if (!eligibility) return null;

  const { canReview, reason } = eligibility;

  if (!canReview) {
    return (
      <div className="lrf">
        <h3 className="lrf__title">Leave a Review</h3>
        {reason === 'not_logged_in' && (
          <div className="lrf__gate">
            <IcLogin />
            <span>
              <Link to="/login" className="lrf__link">Log in</Link> to leave a review for this broker.
            </span>
          </div>
        )}
        {reason === 'cannot_review_self' && (
          <div className="lrf__gate">You cannot review your own profile.</div>
        )}
        {reason === 'already_reviewed' && (
          <div className="lrf__gate lrf__gate--success">
            <IcCheck /> You have already reviewed this broker.
          </div>
        )}
        {reason === 'must_contact_first' && (
          <div className="lrf__gate">
            You can leave a review 30 days after contacting this broker.
          </div>
        )}
      </div>
    );
  }

  if (success) {
    return (
      <div className="lrf">
        <h3 className="lrf__title">Leave a Review</h3>
        <div className="lrf__success">
          <IcCheck />
          <div>
            <strong>Review submitted!</strong>
            <p>Thank you — your review will appear below.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lrf">
      <h3 className="lrf__title">Leave a Review</h3>

      {REVIEW_TEST_MODE && (
        <div className="lrf__test-banner">
          <IcWarn />
          <span>Test Mode — eligibility checks are disabled. Reviews can be left freely.</span>
        </div>
      )}

      <div className="lrf__form">
        <div className="lrf__stars-label">Your rating</div>
        <StarRating value={rating} onChange={setRating} size="large" />

        <div className="lrf__field">
          <label className="lrf__label">Your review</label>
          <textarea
            className="lrf__textarea"
            rows={5}
            maxLength={MAX_CHARS}
            placeholder="Share your experience working with this broker..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="lrf__char-count">
            <span className={text.length < MIN_CHARS && text.length > 0 ? 'lrf__char-count--warn' : ''}>
              {text.length}/{MAX_CHARS}
            </span>
            {text.length < MIN_CHARS && text.length > 0 && (
              <span className="lrf__char-min">minimum {MIN_CHARS} characters</span>
            )}
          </div>
        </div>

        {error && <div className="lrf__error">{error}</div>}

        <button className="lrf__submit" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Review'}
        </button>
      </div>
    </div>
  );
}
